import { useMemo, useRef } from "react";

import { toastLocalizedOperationCatcher } from "./alerts";
import { useAv } from "./av";
import { useQueryParamsManager } from "./routing/hooks/use-query-params-manager";
import { PartialUpdateable } from "./type-utils";
import { ViewLevelFlags, pageUrl, withQueryParams } from "./urls";
import { getUserShortName } from "./user-utils";
import { ensureWindowId } from "./window";

import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { type PresentationBannerColorType } from "components/footer/PresentationBanner";
import { useNameFromPageAccessId } from "components/materials/page/helpers";
import {
  WindowMessages,
  sendMessageToProjectorView,
} from "components/materials/presentation/projector/projector-messaging";
import { ClassSessionType, InteractionMode, PageType } from "components/server-types";
import { toastReleasePage } from "helpers/toasts";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectActiveClassSession,
  selectCurrentClassUserBreakoutGroupId,
  selectCurrentClassUserIsInPersonClass,
  selectCurrentPageId,
  selectCurrentSection,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectIsPracticeClassSession,
  selectIsRecordingAgent,
  selectScreensharingClassUserId,
  selectSelectedPageTabVariant,
  userSelectors,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";

const PRESENTER_ID_KEY = "ForumPresenterTabId";

export const DEFAULT_SCALING_FACTOR = 1.5;
export const PROJECTOR_MAX_HEIGHT = 10000; // Maximum height for the projector view
const PROJECTOR_ZOOM_VALUES = [75, 100, 125, 150, 175, 200];
export const PROJECTOR_ZOOM_ITEMS = PROJECTOR_ZOOM_VALUES.map((value) => ({
  id: value.toString(),
  title: `${value}%`,
}));

const getPresenterWindow = () => localStorage.getItem(PRESENTER_ID_KEY);
const setPresenterWindow = (id: string) => localStorage.setItem(PRESENTER_ID_KEY, id);
export const removePresenterWindow = () => localStorage.removeItem(PRESENTER_ID_KEY);

export const setPresentationWindow = () => {
  const windowName = ensureWindowId();

  setPresenterWindow(windowName);
};

export const isPresentationWindow = () => {
  const presentationWindowId = getPresenterWindow();

  return window.name === presentationWindowId;
};

export const sendSessionUpdateForProjector = () => {
  if (isPresentationWindow()) {
    sendMessageToProjectorView({ type: WindowMessages.SESSION_SYNC });
  }
};

export const updateSession = (session: PartialUpdateable<ClassSessionType>) => {
  return storeApi.class_sessions
    .partial_update(session, { skipToast: true })
    .then(sendSessionUpdateForProjector);
};

export const presentSession = async (
  sessionId: string,
  {
    page,
    isRollup,
    isPractice,
    hasProjectorView,
    accessId = null,
    userId,
  }: {
    page: PageType;
    isRollup?: boolean;
    isPractice?: boolean | null;
    hasProjectorView?: boolean;
    accessId?: string | null;
    userId?: string | null;
  },
) => {
  const session: PartialUpdateable<ClassSessionType> = {
    id: sessionId,
    presented_page_id: page?.id,
    presenting_user_id: page ? userId : null,
    presented_block_id: null,
    presenting_outcomes: false,
  };

  if (isRollup !== undefined) {
    session.presenting_rollup = isRollup;
  }

  if (hasProjectorView !== undefined) {
    session.is_projecting = hasProjectorView;
  }

  if (accessId !== undefined) {
    session.presented_access_id = accessId;
  }

  try {
    await updateSession(session);

    // We just released the page so make a toast
    if (page && !page.released_at && !isPractice) {
      toastReleasePage();
    }
  } catch (e) {
    toastLocalizedOperationCatcher("present_page_failure")(e);
  }
};

export type FeatureOptions = {
  blockId?: string | null;
  accessId?: string | null;
  isPresentingOutcomes?: boolean;
  isRollup?: boolean;
};

export const featurePage = (
  sessionId: string,
  page: PageType,
  {
    blockId,
    accessId,
    isPresentingOutcomes = false,
    isRollup = true, // Most of the time when we feature it's rollup
  }: FeatureOptions,
) => {
  const featureAccessId = page.interaction_mode !== InteractionMode.SHARED_CLASS ? accessId : null;

  return updateSession({
    id: sessionId,
    presented_page_id: page.id,
    presenting_rollup: isRollup,
    presented_access_id: featureAccessId,
    presented_block_id: blockId,
    presenting_outcomes: isPresentingOutcomes,
  }).catch(toastLocalizedOperationCatcher("present_page_failure"));
};

export const stopPresenting = (sessionId: string) => {
  removePresenterWindow();

  return updateSession({
    id: sessionId,
    presented_page_id: null,
    presented_block_id: null,
    presenting_user_id: null,
    presenting_outcomes: false,
    presenting_rollup: false,
    is_projecting: false,
    presented_access_id: null,
  }).catch(toastLocalizedOperationCatcher("stop_presenting_failure"));
};

export const startProjecting = (sessionId: string, extraUpdates: Partial<ClassSessionType>) => {
  const session: PartialUpdateable<ClassSessionType> = {
    ...extraUpdates,
    id: sessionId,
    is_projecting: true,
  };
  return updateSession(session).catch(toastLocalizedOperationCatcher("present_page_failure"));
};

export const stopFeaturing = (sessionId: string) => {
  return updateSession({
    id: sessionId,
    presenting_rollup: false,
    presenting_outcomes: false,
    presented_access_id: null,
    presented_block_id: null,
  }).catch(toastLocalizedOperationCatcher("stop_presenting_failure"));
};

export const takeOverPresenting = (sessionId: string, userId: string) => {
  return updateSession({ id: sessionId, presenting_user_id: userId }).catch(
    toastLocalizedOperationCatcher("stop_presenting_failure"),
  );
};

export enum FeatureStatus {
  NONE = "none",
  BLOCK = "block",
  OUTCOME = "outcome",
  CLASS_RESPONSES = "class_responses",
  CLASS_RESPONSES_WITH_BLOCK = "class_responses_with_block",
  PARTICIPANT_RESPONSE = "participant_response",
  PARTICIPANT_RESPONSE_WITH_BLOCK = "participant_response_with_block",
}

const getFeatureStatus = (classSession: ClassSessionType | null) => {
  if (!classSession) {
    return FeatureStatus.NONE;
  }

  if (classSession.presenting_outcomes) {
    return FeatureStatus.OUTCOME;
  }

  if (classSession.presenting_rollup) {
    if (classSession.presented_block_id) {
      return classSession.presented_access_id
        ? FeatureStatus.PARTICIPANT_RESPONSE_WITH_BLOCK
        : FeatureStatus.CLASS_RESPONSES_WITH_BLOCK;
    }

    return classSession.presented_access_id
      ? FeatureStatus.PARTICIPANT_RESPONSE
      : FeatureStatus.CLASS_RESPONSES;
  }

  return classSession.presented_block_id ? FeatureStatus.BLOCK : FeatureStatus.NONE;
};

export const usePresentation = () => {
  const classSession = useAppSelector(selectActiveClassSession);
  const userId = useAppSelector(selectCurrentUserId);
  const selectedPageTabVariant = useAppSelector(selectSelectedPageTabVariant);
  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  const { getQueryParam } = useQueryParamsManager();
  const followingQueryParam = getQueryParam(FOLLOWING_QUERY_PARAM);
  const pageId = useAppSelector(selectCurrentPageId);
  const prevPresentedPageIdRef = useRef<string | null | undefined>(undefined);

  return useMemo(() => {
    const isPresentingUser = classSession?.presenting_user_id === userId;
    const isPresenting = isPresentingUser && isPresentationWindow();

    // We use presentedPageId to determine if we are in fact presenting
    const presentedPageId =
      isPresentingUser || !classSession?.is_practice ? classSession?.presented_page_id : null;
    const inPresentationMode = !!presentedPageId;
    const isScreensharingOn = !!classSession?.screensharing_class_user_id;
    const featureStatus = getFeatureStatus(classSession);

    const isFollowing =
      (isRecordingAgent && (inPresentationMode || isScreensharingOn)) ||
      (followingQueryParam === "1" && inPresentationMode);

    const presentedPageJustChanged =
      prevPresentedPageIdRef.current !== undefined &&
      prevPresentedPageIdRef.current !== presentedPageId;
    prevPresentedPageIdRef.current = presentedPageId;

    // Only consider disconnected if:
    // 1. User is following
    // 2. They're on a different page than presented
    // 3. The presented page didn't just change (avoid race condition during auto-navigation)
    const isFollowingDisconnected =
      isFollowing && presentedPageId !== pageId && !presentedPageJustChanged;

    return {
      sectionId: classSession?.section_id,
      featureStatus,
      isFollowing,
      isFollowingDisconnected,
      inPresentationMode,
      isPresenting,
      isPresentingUser,
      hasProjectorView: classSession?.is_projecting,
      presentedAccessId: classSession?.presented_access_id,
      presentedBlockId: classSession?.presented_block_id,
      presentingOutcomes: classSession?.presenting_outcomes,
      presentedPageId,
      presentedSessionId: classSession?.id,
      presentingRollup: classSession?.presenting_rollup,
      presentingUserId: classSession?.presenting_user_id,
      unfeaturedTab:
        isPresenting &&
        selectedPageTabVariant === "responses" &&
        featureStatus === FeatureStatus.NONE,
    };
  }, [classSession, userId, isRecordingAgent, followingQueryParam, pageId, selectedPageTabVariant]);
};

export const useProjectorScalingFactor = () => {
  const currentSection = useAppSelector(selectCurrentSection);
  if (!currentSection) {
    return DEFAULT_SCALING_FACTOR;
  }

  return currentSection.projector_zoom / 100;
};

// Used with components/materials/page/banners/NotFollowingPresentationBanner.tsx
export const useShowNotFollowingPresentationBanner = () => {
  const { isFollowing, isFollowingDisconnected, presentedPageId, isPresentingUser } =
    usePresentation();
  const isSmallScreen = !useIsMdOrLarger();
  return (
    isSmallScreen &&
    presentedPageId &&
    (!isFollowing || isFollowingDisconnected) &&
    !isPresentingUser
  );
};

type FooterPresentationBannerTextType = {
  bannerText: string;
  tooltipText?: string | null;
};

export const usePresentationBannerText = (
  viewLevel: ViewLevelFlags,
  showShortText: boolean,
): FooterPresentationBannerTextType => {
  const {
    featureStatus,
    presentedPageId,
    isPresenting,
    isPresentingUser,
    presentedAccessId,
    hasProjectorView,
    unfeaturedTab,
    isFollowing,
    isFollowingDisconnected,
  } = usePresentation();
  const { canAuthorSection } = useCapabilities();
  const participantName = useNameFromPageAccessId(presentedAccessId, presentedPageId);
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);

  if (isPresenting) {
    let tooltipText: string | null = null;

    if (unfeaturedTab) {
      return {
        bannerText: showShortText
          ? t("main_header.feature_status.not_featured_short")
          : t("main_header.feature_status.not_featured"),
      };
    }

    if (presentedAccessId) {
      if (showShortText) {
        return { bannerText: t("main_header.feature_status.featuring_short") };
      }
      if (featureStatus === FeatureStatus.PARTICIPANT_RESPONSE_WITH_BLOCK) {
        return {
          bannerText: t("main_header.feature_status.status.participant_response_with_block", {
            name: participantName,
          }),
          tooltipText: t("main_header.feature_status.tooltip.featured_response"),
        };
      }
      return {
        bannerText: t(`main_header.feature_status.status.${featureStatus}`, {
          name: participantName,
        }),
      };
    }

    // If there's no presentedAccessId, but we are featuring items that do not have a
    // participant/access id, we will display text that says we are featuring that item.
    // Otherwise, if there's no featured item, we will display a handy tooltip text for
    // the instructor to know what's going on when projecting or to try featuring a response.
    if (featureStatus === FeatureStatus.NONE) {
      const tooltipKey = hasProjectorView
        ? "projecting"
        : isPracticeClassSession
          ? "practicing"
          : "presenting";
      tooltipText = t(`main_header.feature_status.tooltip.${tooltipKey}`);
    } else {
      return {
        bannerText: showShortText
          ? t(`main_header.feature_status.featuring_short`)
          : t(`main_header.feature_status.status.${featureStatus}`),
      };
    }

    if (isPracticeClassSession) {
      return {
        bannerText: showShortText
          ? t("presentation.practicing_presentation_short")
          : t("presentation.practicing_presentation"),
        tooltipText,
      };
    }

    return {
      bannerText: showShortText
        ? t("presentation.presentation_in_progress_short")
        : t("presentation.presentation_in_progress"),
      tooltipText,
    };
  }

  // TODO: What if a section is presenting and we're on the home page?
  if (viewLevel.section || viewLevel.home) {
    return {
      bannerText: showShortText
        ? t("presentation.presentation_in_progress_short")
        : t("presentation.presentation_in_progress"),
    };
  }

  if (isPresentingUser) {
    return {
      bannerText: t("main_header.presenting_in_other_tab"),
      tooltipText: t("main_header.move_presentation_tooltip"),
    };
  }

  if (isFollowing) {
    if (!isFollowingDisconnected) {
      return {
        bannerText: showShortText
          ? t("main_header.following_presentation_short")
          : t("main_header.following_presentation"),
      };
    }
    return {
      bannerText: showShortText
        ? t("main_header.not_following_the_presentation_short")
        : t("main_header.not_following_the_presentation"),
    };
  }

  if (canAuthorSection) {
    return { bannerText: t("main_header.not_following_the_presentation") };
  }

  return {
    bannerText: showShortText
      ? t("presentation.presentation_in_progress_short")
      : t("presentation.presentation_in_progress"),
  };
};

export const usePresentationBannerColoring = (): PresentationBannerColorType => {
  const { canAuthorSection } = useCapabilities();
  const { isPresenting, isFollowing, isFollowingDisconnected, presentedPageId, unfeaturedTab } =
    usePresentation();
  const { isInVirtualClass } = useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);

  // Instructor-only
  if (isPracticeClassSession) {
    return "light-gray";
  }

  // Instructor-only
  if (unfeaturedTab) {
    return "orange";
  }

  // Instructor-only
  if (isPresenting) {
    return "green";
  }

  if (isFollowing) {
    // Following and viewing the presented page
    if (!isFollowingDisconnected) {
      // Instructor-only
      if (canAuthorSection) {
        return "green";
      }
      // Student-only
      return isInVirtualClass || isInInPersonClass ? "dark-gray" : "light-green";
    }

    // Following but viewing a different page
    return "orange";
  }

  if (presentedPageId) {
    // Instructor-only
    if (canAuthorSection) {
      return "orange";
    }

    // Not following
    return "black";
  }

  if (isInInPersonClass) {
    return "black";
  }

  return "green";
};

export const useSelectedPageListItemColoring = () => {
  const { canAuthorSection } = useCapabilities();
  const bannerColoring = usePresentationBannerColoring();
  const { unfeaturedTab, isPresenting } = usePresentation();

  if (canAuthorSection) {
    // If the instructor is presenting & is on the window they are presenting,
    // we want to show the orange coloring on the list item if they are viewing
    // a tab or page that is different from what the students are viewing. There
    // is only one case where this can happen so far: when the instructor has
    // clicked on the "Class Responses" tab from the Template tab.
    if (bannerColoring === "orange" && unfeaturedTab && isPresenting) {
      return "selected-orange";
    }
    if (bannerColoring === "green") {
      return "selected-green";
    }
    return "selected-blue";
  }

  if (bannerColoring === "light-green" || bannerColoring === "dark-gray") {
    return "selected-green";
  }
  return "selected-blue";
};

const PRESENTATION_TAG_COLOR_MAP: Record<PresentationBannerColorType, string> = {
  black: "bg-black-tint-20",
  "light-gray": "bg-black-tint-55",
  "light-green": "bg-green-shade-20",
  green: "bg-green-shade-20",
  orange: "bg-orange",
  "dark-gray": "bg-green-shade-20",
};

export const usePresentationCaption = () => {
  const bannerColoring = usePresentationBannerColoring();
  const showShortText = !useIsMdOrLarger();

  const {
    isPresenting,
    isFollowing,
    isFollowingDisconnected,
    presentingUserId,
    unfeaturedTab,
    featureStatus,
    hasProjectorView,
  } = usePresentation();
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const { canAuthorSection } = useCapabilities();
  const presentingUser = useAppSelector((s) => userSelectors.selectById(s, presentingUserId));
  const isFeaturing = featureStatus !== FeatureStatus.NONE;
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);

  // The "Session in Progress" caption has an additional link when a presentation is
  // in progress and the student is not following.
  const sectionId = useAppSelector(selectCurrentSectionId);
  const classSession = useAppSelector(selectActiveClassSession);
  const presentedPageId = classSession?.presented_page_id;
  const presentedPageUrl =
    sectionId && presentedPageId
      ? withQueryParams(pageUrl(sectionId, presentedPageId), {
          [FOLLOWING_QUERY_PARAM]: "1",
        })
      : "";

  let caption = "";
  let linkText = "";
  let linkUrl = "";

  if (isPracticeClassSession) {
    if (isPresenting) {
      caption = t("presentation.practicing_presentation");
    } else {
      caption = t("presentation.caption.practicing");
    }
  } else if (isPresenting) {
    if (unfeaturedTab) {
      caption = t("presentation.caption.presenting_other");
    }

    caption =
      hasProjectorView && !isFeaturing
        ? t("presentation.caption.projecting")
        : t("presentation.caption.presenting");
  } else if (isFollowingDisconnected) {
    if (canAuthorSection) {
      // NOTE: this case no longer happens because the following param is removed when
      // an instructor who is following navigates away from the presented page
      caption = t("presentation.caption.instructor_presenting", {
        name: getUserShortName(presentingUser),
      });
    } else {
      caption = t("presentation.caption.following_disconnected");
      linkText = t("common.return");
      linkUrl = presentedPageUrl;
    }
  } else if (isFollowing) {
    caption = t(
      showShortText ? "presentation.caption.following_short" : "presentation.caption.following",
    );
  } else if (presentedPageId) {
    caption = t(
      showShortText
        ? "presentation.presentation_in_progress_short"
        : "presentation.presentation_in_progress",
    );

    if (presentedPageId) {
      linkText = t("main_header.follow_short");
      linkUrl = presentedPageUrl;
    }
  } else if (isInInPersonClass) {
    caption = t("presentation.caption.in_person_session");
  }

  return {
    captionText: caption,
    captionBgColor: PRESENTATION_TAG_COLOR_MAP[bannerColoring] || "bg-black-tint-55",
    linkText,
    linkUrl,
  };
};

const BORDER_PRESENTATION_COLOR_MAP: Record<PresentationBannerColorType, string> = {
  black: "border-black-tint-70",
  "light-gray": "border-black-tint-70",
  "light-green": "border-green-tint-20",
  green: "border-green-tint-20",
  orange: "border-orange-tint-20",
  "dark-gray": "border-green-tint-20",
};

export const useBorderedPresentationColoring = () => {
  const bannerColoring = usePresentationBannerColoring();

  return BORDER_PRESENTATION_COLOR_MAP[bannerColoring] || "border-black-tint-90";
};

// The presentation caption is shown in the PageView component for virtual classes
// and in the MainHeader and MobileMainHeader components for in-person classes.
export const useShouldShowPresentationCaption = (isMainHeader: boolean) => {
  const { isInVirtualClass } = useAv();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isInVirtualClassOrObserving = isInVirtualClass || isObservingClass;
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);

  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const isBreakoutActive = useAppSelector(selectIsInBreakout);
  const currentClassUserBreakoutGroupId = useAppSelector(selectCurrentClassUserBreakoutGroupId);
  const isInBreakoutRoom =
    isBreakoutActive && isInVirtualClass && !!currentClassUserBreakoutGroupId;

  const {
    presentedPageId,
    isPresenting,
    featureStatus,
    hasProjectorView,
    unfeaturedTab,
    isFollowing,
    isFollowingDisconnected,
  } = usePresentation();

  const isFeaturingAndIsFollowingOrPresenting =
    featureStatus !== FeatureStatus.NONE &&
    (isFollowing || isPresenting) &&
    !isFollowingDisconnected;

  const commonChecks =
    !isFeaturingAndIsFollowingOrPresenting &&
    (!(isPresenting && hasProjectorView) || unfeaturedTab);

  if (isMainHeader) {
    return !isInVirtualClassOrObserving && isInInPersonClass && commonChecks;
  }

  return (
    isInVirtualClass &&
    !isInBreakoutRoom &&
    (presentedPageId || isPracticeClassSession) &&
    !screensharingClassUserId &&
    commonChecks
  );
};
